import { create } from 'zustand';
import type { TimeRangeValue } from '@bbpos/ui';
import { DEFAULT_TIMEZONE } from '@bbpos/types';
import { shiftDate, todayIn } from '@/lib/store-clock';

/**
 * Dashboard-wide period preset. Drives every widget that supports a range
 * (Top Categories, Sales Heatmap, future range-aware summaries). The KPI
 * cards remain "today vs yesterday" — changing those semantics belongs in
 * a follow-up that rewrites getSummary on the backend.
 */
export type DashboardPeriod =
  | { preset: 'today' }
  | { preset: 'yesterday' }
  | { preset: 'last7' }
  | { preset: 'last30' }
  | { preset: 'custom'; startDate: string; endDate: string };

interface DashboardState {
  /** Selected location filter — undefined = all locations */
  selectedLocationId: string | undefined;
  /** Time range for Sales Wave chart — kept separately because Sales Wave
   *  shows hourly intraday data and pairs only with today/yesterday/last7. */
  timeRange: TimeRangeValue;
  /** Dashboard-wide period — applies to range-aware widgets. */
  period: DashboardPeriod;
  /** Whether the staff insight banner is dismissed for this session */
  insightDismissed: boolean;
  /** Active dashboard tab */
  activeTab: string;

  setSelectedLocationId: (id: string | undefined) => void;
  setTimeRange: (value: TimeRangeValue) => void;
  setPeriod: (value: DashboardPeriod) => void;
  dismissInsight: () => void;
  setActiveTab: (tab: string) => void;
}

export const useDashboardStore = create<DashboardState>((set) => ({
  selectedLocationId: undefined,
  timeRange: { preset: 'today' },
  period: { preset: 'today' },
  insightDismissed: false,
  activeTab: 'overview',

  setSelectedLocationId: (id) => set({ selectedLocationId: id }),
  setTimeRange: (value) => set({ timeRange: value }),
  setPeriod: (value) => set({ period: value }),
  dismissInsight: () => set({ insightDismissed: true }),
  setActiveTab: (tab) => set({ activeTab: tab }),
}));

/**
 * Resolve a DashboardPeriod to YYYY-MM-DD start/end strings on the store's
 * clock (`timeZone`, T18 — see `useStoreTimezone`); IST when not given.
 * Widgets call this directly so the resolution lives in one place.
 */
export function resolvePeriod(
  period: DashboardPeriod,
  timeZone: string = DEFAULT_TIMEZONE,
  now: Date = new Date(),
): { startDate: string; endDate: string } {
  const today = todayIn(timeZone, now);

  if (period.preset === 'today') return { startDate: today, endDate: today };
  if (period.preset === 'yesterday') {
    const y = shiftDate(today, -1);
    return { startDate: y, endDate: y };
  }
  if (period.preset === 'last7') return { startDate: shiftDate(today, -6), endDate: today };
  if (period.preset === 'last30') return { startDate: shiftDate(today, -29), endDate: today };
  return { startDate: period.startDate, endDate: period.endDate };
}
